import { EntitlementFeatureKey, Entitlements } from '../services/entitlementService';

export type NavRole = 'admin' | 'sales' | 'employee';

export type NavIconKey =
  | 'dashboard'
  | 'leads'
  | 'imports'
  | 'prospects'
  | 'clients'
  | 'upsell'
  | 'campaigns'
  | 'email'
  | 'performance'
  | 'chatbot'
  | 'website'
  | 'settings';

export type NavMatchFn = (pathname: string, searchParams: URLSearchParams) => boolean;

export interface NavItemConfig {
  id: string;
  name: string;
  href?: string;
  icon?: NavIconKey;
  roles?: NavRole[];
  /** If set, the item is hidden when this entitlement flag is explicitly false. */
  entitlement?: EntitlementFeatureKey;
  match?: NavMatchFn;
  children?: NavItemConfig[];
}

export interface NavSectionConfig {
  id: string;
  label?: string;
  items: NavItemConfig[];
}

export interface ResolvedNavItem extends NavItemConfig {
  current: boolean;
  children?: ResolvedNavItem[];
}

export interface ResolvedNavSection extends NavSectionConfig {
  items: ResolvedNavItem[];
}

const isLeadsPipeline: NavMatchFn = (pathname, searchParams) =>
  pathname === '/leads' && searchParams.get('tab') !== 'inbound';

const isLeadsInbound: NavMatchFn = (pathname, searchParams) =>
  pathname === '/leads' && searchParams.get('tab') === 'inbound';

const isLeadImports: NavMatchFn = (pathname) => pathname.startsWith('/lead-imports');
const isLeadSuggestions: NavMatchFn = (pathname) => pathname.startsWith('/leads/suggestions');

const isLeadsGroup: NavMatchFn = (pathname, searchParams) =>
  isLeadsPipeline(pathname, searchParams) ||
  isLeadsInbound(pathname, searchParams) ||
  isLeadSuggestions(pathname, searchParams) ||
  isLeadImports(pathname, searchParams) ||
  pathname.startsWith('/leads/');

const isEmailSettings: NavMatchFn = (pathname) => pathname === '/settings/email';

const isEmailPerformance: NavMatchFn = (pathname) => pathname.startsWith('/settings/email/performance');

const isSequences: NavMatchFn = (pathname) => pathname.startsWith('/sequences');

const isOutreachGroup: NavMatchFn = (pathname, searchParams) =>
  pathname.startsWith('/campaigns') ||
  isSequences(pathname, searchParams) ||
  isEmailSettings(pathname, searchParams) ||
  isEmailPerformance(pathname, searchParams);

export const navigationSections: NavSectionConfig[] = [
  {
    id: 'overview',
    items: [
      {
        id: 'dashboard',
        name: 'Dashboard',
        href: '/',
        icon: 'dashboard',
        roles: ['admin'],
        match: (pathname) => pathname === '/',
      },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    items: [
      {
        id: 'leads',
        name: 'Leads',
        href: '/leads',
        icon: 'leads',
        roles: ['admin', 'sales'],
        match: isLeadsGroup,
        children: [
          {
            id: 'leads-pipeline',
            name: 'Pipeline',
            href: '/leads?tab=pipeline',
            match: isLeadsPipeline,
          },
          {
            id: 'leads-inbound',
            name: 'Inbound',
            href: '/leads?tab=inbound',
            match: isLeadsInbound,
          },
          {
            id: 'lead-suggestions',
            name: 'Suggestions',
            href: '/leads/suggestions',
            entitlement: 'lead_suggestions_enabled',
            match: isLeadSuggestions,
          },
          {
            id: 'lead-imports',
            name: 'Imports',
            href: '/leads?import=choose',
            icon: 'imports',
            entitlement: 'csv_import_enabled',
            match: isLeadImports,
          },
        ],
      },
      {
        id: 'prospects',
        name: 'Prospects',
        href: '/prospects',
        icon: 'prospects',
        match: (pathname) => pathname === '/prospects',
      },
      {
        id: 'clients',
        name: 'Clients',
        href: '/clients',
        icon: 'clients',
        match: (pathname) => pathname === '/clients',
      },
      {
        id: 'tasks',
        name: 'Tasks',
        href: '/tasks',
        icon: 'performance',
        match: (pathname) => pathname === '/tasks',
      },
      {
        id: 'upsell-pipeline',
        name: 'Upsell Pipeline',
        href: '/pipeline/upsells',
        icon: 'upsell',
        roles: ['admin', 'sales'],
        entitlement: 'upsells_enabled',
        match: (pathname) => pathname === '/pipeline/upsells',
      },
    ],
  },
  {
    id: 'outreach',
    label: 'Outreach',
    items: [
      {
        id: 'outreach',
        name: 'Outreach',
        icon: 'campaigns',
        match: isOutreachGroup,
        children: [
          {
            id: 'campaigns',
            name: 'Campaigns',
            href: '/campaigns',
            roles: ['admin', 'sales'],
            entitlement: 'campaigns_enabled',
            match: (pathname) => pathname.startsWith('/campaigns'),
          },
          {
            id: 'sequences',
            name: 'Sequences',
            href: '/sequences',
            roles: ['admin', 'sales'],
            entitlement: 'sequences_enabled',
            match: isSequences,
          },
          {
            id: 'email',
            name: 'Email',
            href: '/settings/email',
            icon: 'email',
            entitlement: 'campaigns_enabled',
            match: isEmailSettings,
          },
          {
            id: 'email-performance',
            name: 'Performance',
            href: '/settings/email/performance',
            icon: 'performance',
            entitlement: 'advanced_analytics_enabled',
            match: isEmailPerformance,
          },
        ],
      },
    ],
  },
  {
    id: 'channels',
    label: 'Channels',
    items: [
      {
        id: 'inbox',
        name: 'Inbox',
        href: '/chatbot/inbox',
        icon: 'chatbot',
        roles: ['admin', 'sales'],
        match: (pathname) => pathname.startsWith('/chatbot/inbox'),
      },
      {
        id: 'chatbot',
        name: 'Chatbot',
        href: '/chatbot/settings',
        icon: 'chatbot',
        roles: ['admin', 'sales'],
        entitlement: 'chatbot_enabled',
        match: (pathname) =>
          pathname.startsWith('/chatbot') && !pathname.startsWith('/chatbot/inbox'),
      },
      {
        id: 'website',
        name: 'Website',
        href: '/settings/website',
        icon: 'website',
        roles: ['admin'],
        entitlement: 'advanced_analytics_enabled',
        match: (pathname) => pathname === '/settings/website',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    items: [
      {
        id: 'organization',
        name: 'Organization',
        href: '/tenant',
        icon: 'settings',
        match: (pathname) => pathname === '/tenant',
      },
    ],
  },
];

export const isNavItemVisible = (item: NavItemConfig, role: NavRole | undefined): boolean => {
  if (!item.roles || item.roles.length === 0) return true;
  if (!role) return false;
  return item.roles.includes(role);
};

/**
 * Entitlement gate for a nav item. Fail-open: an item is hidden only when its
 * entitlement flag is *explicitly* false. Missing entitlements (still loading)
 * or an undefined flag leave the item visible, matching the backend's
 * kill-switch/full-access default.
 */
export const isNavItemEntitled = (
  item: NavItemConfig,
  entitlements: Entitlements | null | undefined
): boolean => {
  if (!item.entitlement) return true;
  if (!entitlements) return true;
  return entitlements[item.entitlement] !== false;
};

const resolveItem = (
  item: NavItemConfig,
  pathname: string,
  searchParams: URLSearchParams,
  role: NavRole | undefined,
  entitlements: Entitlements | null | undefined
): ResolvedNavItem | null => {
  if (!isNavItemVisible(item, role)) return null;
  if (!isNavItemEntitled(item, entitlements)) return null;

  const resolvedChildren = (item.children ?? [])
    .map((child) => resolveItem(child, pathname, searchParams, role, entitlements))
    .filter((child): child is ResolvedNavItem => child !== null);

  if (item.children?.length && resolvedChildren.length === 0) return null;

  const selfCurrent = item.match?.(pathname, searchParams) ?? false;
  const childCurrent = resolvedChildren.some((child) => child.current);

  return {
    ...item,
    children: resolvedChildren.length > 0 ? resolvedChildren : undefined,
    current: selfCurrent || childCurrent,
  };
};

export const resolveNavigationSections = (
  role: NavRole | undefined,
  pathname: string,
  search: string,
  entitlements?: Entitlements | null
): ResolvedNavSection[] => {
  const searchParams = new URLSearchParams(search);

  return navigationSections
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => resolveItem(item, pathname, searchParams, role, entitlements))
        .filter((item): item is ResolvedNavItem => item !== null),
    }))
    .filter((section) => section.items.length > 0);
};

export const collectExpandableGroupIds = (sections: ResolvedNavSection[]): string[] => {
  const ids: string[] = [];

  const walk = (items: ResolvedNavItem[]) => {
    for (const item of items) {
      if (item.children?.length) {
        if (item.current) ids.push(item.id);
        walk(item.children);
      }
    }
  };

  for (const section of sections) {
    walk(section.items);
  }

  return ids;
};
